import { Alert, Button, Card, Space, Typography } from "antd";
import { useEffect, useState } from "react";

import request from "@/api";
import { DEFAULT_DIALOG_WIDTH } from "@/config";
import { useUserStore } from "@/views/stores";
import { Dialog as ZhDialog } from "zhihao-ui-react";
import { BannerFormPage } from "./BannerFormPage";

interface BannerRecord {
    id?: number;
    fileId?: number;
    sort?: number;
    groupType?: number;
}

interface BannerFile {
    id?: number;
    path?: string;
}

interface BannerSortRequest {
    sortList: Array<{ id: number; sort: number }>;
}

export interface BannerPageProps {
    loadBanners?: (type: number) => Promise<BannerRecord[]>;
    loadGroupedBanners?: () => Promise<Record<number, BannerRecord[]>>;
    loadBannerFile?: (id: number) => Promise<BannerFile>;
    updateSort?: (params: BannerSortRequest) => Promise<unknown>;
    deleteBanner?: (id: number) => Promise<unknown>;
    permissions?: string[];
}

const MOBILE_POSITION = 1;
const GROUPS = [
    { code: 1, title: "首页顶部", slots: 4 },
    { code: 2, title: "首页活动推荐", slots: 4 },
    { code: 3, title: "寄售商品顶部", slots: 1 },
    { code: 4, title: "捡漏商品顶部", slots: 1 }
];

/** 复用 Vue 移动端广告位列表接口。 */
export async function loadMobileBanners(type: number): Promise<BannerRecord[]> {
    const response = await request.service.get<BannerRecord[]>(`/dataService/admin/banner/findList?type=${type}`);
    return response.data;
}

/** 复用 Vue 链云砂石广告分组接口。 */
export async function loadMobileGroupedBanners(): Promise<Record<number, BannerRecord[]>> {
    const response = await request.service.get<Record<number, BannerRecord[]>>(
        "/dataService/admin/banner/group/findList"
    );
    return response.data;
}

/** 复用 Vue 按文件 ID 查询轮播图媒体信息的接口。 */
export async function loadMobileBannerFile(id: number): Promise<BannerFile> {
    const response = await request.service.get<BannerFile>(`/dataService/admin/file/${id}`);
    return response.data;
}

/** 复用 Vue 移动端广告排序更新接口。 */
export async function updateMobileBannerSort(params: BannerSortRequest): Promise<unknown> {
    const response = await request.service.put("/dataService/admin/banner/update-sort", params);
    return response.data;
}

/** 复用 Vue 移动端轮播图删除接口。 */
export async function deleteMobileBanner(id: number): Promise<unknown> {
    const response = await request.service.delete(`/dataService/admin/banner/${id}`);
    return response.data;
}

/** 移动端广告位首屏，先迁移 Vue 的四个区域和空广告位展示。 */
export function BannerPage({
    loadBanners = loadMobileBanners,
    loadGroupedBanners = loadMobileGroupedBanners,
    loadBannerFile = loadMobileBannerFile,
    updateSort = updateMobileBannerSort,
    deleteBanner = deleteMobileBanner,
    permissions
}: BannerPageProps) {
    // 沿用 Vue 全局封禁态，广告编辑、删除、上传入口保留但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 对齐旧版 bannerImg.vue 的 v-auth，只有广告写权限用户可见写操作入口。
    const canManage = (permissions ?? storePermissions).includes("ROLE_SS_AD_W");
    const [banners, setBanners] = useState<BannerRecord[]>([]);
    const [groupedBanners, setGroupedBanners] = useState<Record<number, BannerRecord[]>>({});
    const [bannerFiles, setBannerFiles] = useState<Record<number, BannerFile & { loadFailed?: boolean }>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [editor, setEditor] = useState<{ id?: number; groupType: number; sort: number } | null>(null);

    useEffect(() => {
        let disposed = false;
        void Promise.all([loadBanners(MOBILE_POSITION), loadGroupedBanners()])
            .then(([list, grouped]) => {
                if (disposed) return;
                setBanners(list || []);
                setGroupedBanners(grouped || {});
                setBannerFiles({});
                // Vue 在每个已存在的广告卡片挂载时按 fileId 查询文件路径；单个文件失败不影响其他卡片。
                const records = [...(list || []), ...Object.values(grouped || {}).flat()];
                const fileIds = [...new Set(records.map(item => item.fileId).filter((id): id is number => id != null))];
                void Promise.all(
                    fileIds.map(async id => {
                        try {
                            const file = await loadBannerFile(id);
                            return [id, { ...file, loadFailed: false }] as const;
                        } catch {
                            return [id, { id, loadFailed: true }] as const;
                        }
                    })
                ).then(files => {
                    if (disposed) return;
                    setBannerFiles(Object.fromEntries(files));
                });
            })
            .catch(() => {
                if (!disposed) setErrorMessage("广告位加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadBannerFile, loadBanners, loadGroupedBanners, reloadToken]);

    const getItems = (groupCode: number): BannerRecord[] => {
        if (groupCode === 1 || groupCode === 2) return groupedBanners[groupCode] || [];
        return banners.filter(item => item.groupType === groupCode);
    };

    const saveSort = async () => {
        const sortGroup = (items: BannerRecord[]) =>
            items.filter(item => item.id != null).map((item, index) => ({ id: item.id!, sort: index + 1 }));
        // Vue 分别为首页顶部和活动推荐组内重新编号，再合并成一个请求数组。
        const sortList = [...sortGroup(getItems(1)), ...sortGroup(getItems(2))];
        try {
            await updateSort({ sortList });
            setErrorMessage(null);
            setSuccessMessage("已保存");
        } catch {
            setSuccessMessage(null);
            setErrorMessage("广告排序保存失败");
        }
    };

    const removeBanner = async (id?: number) => {
        if (id == null) return;
        // Vue confirmable 在删除前要求确认，取消时不应触发删除请求。
        if (!window.confirm("是否删除，删除后该轮播图将不再展示")) return;
        try {
            await deleteBanner(id);
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("轮播图删除失败");
        }
    };

    return (
        <section aria-label="banner-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <Typography.Title className="mb-4 mt-0" level={4}>
                    移动端广告位
                </Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {successMessage ? <Alert message={successMessage} role="alert" type="success" /> : null}
                <Button aria-label="保存排序" type="primary" onClick={() => void saveSort()}>
                    保存排序
                </Button>
                <Space className="w-full" direction="vertical" size="large">
                    {GROUPS.map(group => {
                        const items = getItems(group.code);
                        return (
                            <section key={group.code} aria-label={group.title}>
                                <Typography.Title level={5}>{group.title}</Typography.Title>
                                <div className="grid grid-cols-4 gap-4">
                                    {Array.from({ length: group.slots }, (_, index) => {
                                        const item = items[index];
                                        return (
                                            <Card key={`${group.code}-${index}`} size="small">
                                                {item?.id ? (
                                                    <>
                                                        {item.fileId != null && bannerFiles[item.fileId]?.path ? (
                                                            <img
                                                                alt={`移动端广告图片 ${item.fileId}`}
                                                                className="mb-2 max-h-40 w-full object-contain"
                                                                src={bannerFiles[item.fileId].path}
                                                            />
                                                        ) : null}
                                                        {item.fileId != null && bannerFiles[item.fileId]?.loadFailed ? (
                                                            <Alert
                                                                className="mb-2"
                                                                message="图片加载失败"
                                                                type="warning"
                                                            />
                                                        ) : null}
                                                        <Typography.Text>图片文件 {item.fileId ?? "-"}</Typography.Text>
                                                        {canManage ? (
                                                            <>
                                                                <Button
                                                                    aria-label={`编辑轮播图 ${group.title} ${index + 1}`}
                                                                    disabled={banButton}
                                                                    type="link"
                                                                    onClick={() =>
                                                                        setEditor({
                                                                            id: item.id,
                                                                            groupType: group.code,
                                                                            sort: index + 1
                                                                        })
                                                                    }
                                                                >
                                                                    编辑
                                                                </Button>
                                                                <Button
                                                                    aria-label={`删除轮播图 ${group.title} ${index + 1}`}
                                                                    disabled={banButton}
                                                                    type="link"
                                                                    onClick={() => void removeBanner(item.id)}
                                                                >
                                                                    删除
                                                                </Button>
                                                            </>
                                                        ) : null}
                                                    </>
                                                ) : canManage ? (
                                                    <Button
                                                        aria-label={`上传图片 ${group.title} ${index + 1}`}
                                                        disabled={banButton}
                                                        onClick={() =>
                                                            setEditor({ groupType: group.code, sort: index + 1 })
                                                        }
                                                    >
                                                        上传图片
                                                    </Button>
                                                ) : null}
                                            </Card>
                                        );
                                    })}
                                </div>
                            </section>
                        );
                    })}
                </Space>
                {/* 对齐旧版 imgDialog.vue：编辑表单必须位于 zhihao-ui-react Dialog 内，并保留弹层尺寸与挂载语义。 */}
                <ZhDialog
                    appendToBody
                    destroyOnClose
                    open={editor != null}
                    title={editor?.id == null ? "上传轮播图" : "修改轮播图"}
                    width={DEFAULT_DIALOG_WIDTH}
                    onCancel={() => setEditor(null)}
                >
                    {editor ? (
                        <BannerFormPage
                            id={editor.id}
                            pos={MOBILE_POSITION}
                            groupType={editor.groupType}
                            sort={editor.sort}
                            onSaved={() => {
                                setEditor(null);
                                setReloadToken(value => value + 1);
                            }}
                        />
                    ) : null}
                </ZhDialog>
            </Card>
        </section>
    );
}

export default BannerPage;
